import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ConversationLogDto,
  CursorMetaDto,
} from '@shared/services/conversationApiService/apiService';
import {
  flattenConversationPages,
  pagesHaveLog,
} from '@shared/services/conversationApiService/conversationLogs.utils';
import {
  ConversationLogsArg,
  ConversationLogsEndpoint,
  conversationLogsApi,
  pushConversationLog,
} from '@shared/services/conversationApiService/conversationLogsApi';
import {
  acquireRealtimeSocket,
  type RealtimeSocketHandle,
} from '@shared/services/realtimeSocketManager';
import { useAppDispatch } from '@shared/store';

interface WsException {
  status: string;
  message: string;
}

/**
 * The whole history of a conversation in the RTK Query cache, with live messages appended to it.
 * The user and the super-admin monitor differ only by the REST endpoint.
 */
export const useRealtimeConversationLogs = (
  endpoint: ConversationLogsEndpoint,
  conversationId: string,
  onError?: <T>(error: T) => void,
) => {
  const dispatch = useAppDispatch();
  const [realtime, setRealtime] = useState<RealtimeSocketHandle | null>(null);

  const queryArg = useMemo<ConversationLogsArg | undefined>(
    () => (conversationId ? { conversationId } : undefined),
    [conversationId],
  );

  // currentData, not data: data keeps the previous conversation while the next one loads
  const { currentData, error, hasNextPage, isFetching, fetchNextPage } =
    conversationLogsApi.endpoints[endpoint].useInfiniteQuery(queryArg ?? skipToken, {
      refetchOnMountOrArgChange: true,
    });

  // The monitor shows the whole history: keep loading pages until the last one.
  // A failed page stops here instead of retrying in a loop.
  useEffect(() => {
    if (hasNextPage && !isFetching && !error) void fetchNextPage();
  }, [error, fetchNextPage, hasNextPage, isFetching]);

  // Live lines the cache may lose: before the first page is stored updateQueryData does nothing,
  // and a running request replaces the pages with what it read. Each line is held until a settled
  // cache has it, and written again after every request.
  const pendingLogs = useRef(new Map<string, ConversationLogDto>());

  useEffect(() => {
    const logs = pendingLogs.current;
    return () => logs.clear();
  }, [conversationId]);

  useEffect(() => {
    if (!queryArg || !currentData || isFetching) return;

    const logs = pendingLogs.current;
    logs.forEach((log, id) => {
      if (pagesHaveLog(currentData.pages, id)) logs.delete(id);
      else dispatch(pushConversationLog(endpoint, queryArg, log));
    });
  }, [currentData, dispatch, endpoint, isFetching, queryArg]);

  const messages = useMemo(() => flattenConversationPages(currentData?.pages ?? []), [currentData]);
  const meta: CursorMetaDto | undefined = currentData?.pages[currentData.pages.length - 1]?.meta;

  useEffect(() => {
    const handle = acquireRealtimeSocket(import.meta.env.VITE_CONVERSATION_API_URL);
    setRealtime(handle);

    return () => {
      handle.release();
      setRealtime(null);
    };
  }, []);

  useEffect(() => {
    if (!realtime || !conversationId) return;

    return realtime.joinRoom('subscribe_to_conversation_message', { conversationId });
  }, [realtime, conversationId]);

  useEffect(() => {
    if (!realtime || !queryArg) return;

    const onConversation = (value: { result: ConversationLogDto }) => {
      // The shared socket can still be in the previous conversation's room until it reconnects
      if (value.result?.conversationId !== queryArg.conversationId) return;

      pendingLogs.current.set(value.result.id, value.result);
      dispatch(pushConversationLog(endpoint, queryArg, value.result));
    };
    const onException = (wsError: WsException) => {
      onError?.(wsError);
    };

    const unsubscribers = [
      realtime.on('conversation_message', onConversation),
      realtime.on('exception', onException),
      realtime.onMissedPushes(() =>
        dispatch(
          conversationLogsApi.util.invalidateTags([
            { type: 'ConversationLogs', id: queryArg.conversationId },
          ]),
        ),
      ),
    ];

    return () => unsubscribers.forEach((off) => off());
  }, [realtime, queryArg, endpoint, dispatch, onError]);

  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  return {
    // Until every page of the history is in, or a page failed (reported through onError)
    initialLoading: !error && (!currentData || !!hasNextPage),
    conversationData: { data: messages, meta },
  };
};
